import { APPROVAL_PREVIEW_KIND_LABELS } from '@fos/shared/labels';
import { ChevronLeft, SearchX } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { Page } from '../../../ui/Page';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { Dot } from '../../../ui/Status';
import { TOUCH_TEXT } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { approvalMeta } from '../approval-format';
import { ApprovalExpanded } from '../components/ApprovalCard';
import { Facts } from '../components/ApprovalPreview';
import { useApprovalDetail } from '../hooks';

/**
 * Szczegóły akceptacji (`/akceptacje/:id`; Figma 03 mobile „wdrożenie”, 3021:2731): powrót do listy, etykieta
 * „Akceptacja · rodzaj”, tytuł, „co się stanie”, fakty (klucz–wartość), „Pełne szczegóły” (podgląd), na mobile duży
 * przycisk na dole i „Nie teraz”. Ten sam ekran dla linków z Inboxu i powiadomień.
 */
export function ApprovalDetailPage() {
  const access = useAccess();
  if (!access.can('approvals')) return <ModulePage module="approvals" />;
  return <ApprovalDetailView />;
}

function ApprovalDetailView() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const detail = useApprovalDetail(id);
  const [details, setDetails] = useState<boolean | null>(null);

  const back = (
    <Link
      to="/akceptacje"
      className="inline-flex items-center gap-1 rounded-xs text-body-s text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-accent max-md:min-h-11"
    >
      <ChevronLeft size={18} strokeWidth={1.75} aria-hidden />
      Akceptacje
    </Link>
  );

  if (detail.isPending) {
    return (
      <Page width="narrow">
        {back}
        <div role="status" aria-label="Wczytywanie" className="mt-6 flex flex-col gap-3">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-7 w-3/4" />
          <Skeleton className="h-24 w-full" />
        </div>
      </Page>
    );
  }
  if (detail.isError || !detail.data) {
    const notFound = appErrorCode(detail.error) === 'NOT_FOUND';
    return (
      <Page width="narrow">
        {back}
        <Section className="mt-6">
          {notFound ? (
            <EmptyState
              icon={SearchX}
              title="Nie znaleziono akcji"
              description="Mogła zostać usunięta albo nie masz do niej dostępu."
              action={
                <Button variant="secondary" onClick={() => navigate('/akceptacje')}>
                  Wróć do Akceptacji
                </Button>
              }
            />
          ) : (
            <ErrorState onRetry={() => void detail.refetch()} retrying={detail.isFetching} />
          )}
        </Section>
      </Page>
    );
  }

  const a = detail.data;
  const pending = a.status === 'pending' && a.displayStatus === 'pending';
  const hasFacts = a.preview.facts.length > 0;
  // fakty na górze (Figma: „Strona”, „Zmiana”); pełny podgląd domyślnie otwarty, poza wdrożeniem z samymi faktami
  const showDetails = details ?? !(a.preview.kind === 'deploy' && hasFacts);
  // wdrożenie (Figma 03 mobile): pytanie w nagłówku i opis wyniku pod nim („AI poprawiło walidację…”)
  const deploy = a.preview.kind === 'deploy' ? a.preview : null;
  const lead = deploy?.description ?? null;

  return (
    <Page width="narrow">
      {back}
      <p className="mt-6 flex items-center gap-2 text-caption text-fg-muted">
        <Dot tone="accent" />
        Akceptacja · {APPROVAL_PREVIEW_KIND_LABELS[a.previewKind]}
      </p>
      <h1 className="mt-2 text-h3 text-fg">{deploy?.question || a.summary}</h1>
      <p className="mt-1 text-caption text-fg-muted">{approvalMeta(a)}</p>
      {/* treść akceptacji w białej sekcji (DECISIONS 2026-10-07 „Nowy układ wizualny”); nagłówek na tle */}
      <Section className="mt-5">
        {lead && <p className="mt-4 text-body-m text-fg-secondary md:text-body-s">{lead}</p>}
        {a.effects && pending && (
          <p className={cn('text-body-m text-fg-secondary md:text-body-s', lead ? 'mt-2' : 'mt-4')}>{a.effects.text}</p>
        )}
        {hasFacts && (
          <div className="mt-5">
            <Facts facts={a.preview.facts} />
          </div>
        )}
        {a.preview.kind !== 'generic' && (
          <button
            type="button"
            aria-expanded={showDetails}
            onClick={() => setDetails(!showDetails)}
            className={`mt-4 rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
          >
            {showDetails ? 'Ukryj szczegóły' : 'Pełne szczegóły'}
          </button>
        )}
        <div className="mt-4">
          <ApprovalExpanded
            detail={a}
            layout="page"
            showFacts={false}
            showPreview={showDetails}
            showDescription={!lead}
          />
        </div>
      </Section>
      {pending && (
        <div className="mt-2 flex justify-center md:hidden">
          <Button variant="ghost" className="max-md:h-11" onClick={() => navigate('/akceptacje')}>
            Nie teraz
          </Button>
        </div>
      )}
    </Page>
  );
}
